import * as i1 from '@angular/cdk/tree';
import { CdkTreeNode, CdkTreeNodeDef, CdkNestedTreeNode, CDK_TREE_NODE_OUTLET_NODE, CdkTreeNodePadding, CdkTreeNodeOutlet, CdkTree, CdkTreeNodeToggle, CdkTreeModule } from '@angular/cdk/tree';
import * as i0 from '@angular/core';
import { numberAttribute, booleanAttribute, Directive, Attribute, Input, Inject, Optional, Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, NgModule } from '@angular/core';
import { MatCommonModule } from '@angular/material/core';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, merge } from 'rxjs';
import { take, map } from 'rxjs/operators';

/**
 * Determinte if argument TreeKeyManager is the NoopTreeKeyManager. This function is safe to use with SSR.
 */
function isNoopTreeKeyManager(keyManager) {
  return !!keyManager._isNoopTreeKeyManager;
}
/**
 * Wrapper for the CdkTree node with Material design styles.
 */
let MatTreeNode = /*#__PURE__*/(() => {
  class MatTreeNode extends CdkTreeNode {
    /**
     * The tabindex of the tree node.
     *
     * @deprecated By default MatTreeNode manages focus using TreeKeyManager instead of tabIndex.
     *   Recommend to avoid setting tabIndex directly to prevent TreeKeyManager form getting into
     *   an unexpected state. Tabindex to be removed in a future version.
     * @breaking-change 21.0.0 Remove this attribute.
     */
    get tabIndexInputBinding() {
      return this._tabIndexInputBinding;
    }
    set tabIndexInputBinding(value) {
      // If the specified tabIndex value is null or undefined, fall back to the default value.
      this._tabIndexInputBinding = value;
    }
    _getTabindexAttribute() {
      if (isNoopTreeKeyManager(this._tree._keyManager)) {
        return this.tabIndexInputBinding;
      }
      return this._tabindex;
    }
    /**
     * Whether the component is disabled.
     *
     * @deprecated This is an alias for `isDisabled`.
     * @breaking-change 21.0.0 Remove this input
     */
    get disabled() {
      return this.isDisabled;
    }
    set disabled(value) {
      this.isDisabled = value;
    }
    constructor(elementRef, tree,
    /**
     * The tabindex of the tree node.
     *
     * @deprecated By default MatTreeNode manages focus using TreeKeyManager instead of tabIndex.
     *   Recommend to avoid setting tabIndex directly to prevent TreeKeyManager form getting into
     *   an unexpected state. Tabindex to be removed in a future version.
     * @breaking-change 21.0.0 Remove this attribute.
     */
    tabIndex) {
      super(elementRef, tree);
      /**
       * The default tabindex of the tree node.
       *
       * @deprecated By default MatTreeNode manages focus using TreeKeyManager instead of tabIndex.
       *   Recommend to avoid setting tabIndex directly to prevent TreeKeyManager form getting into
       *   an unexpected state. Tabindex to be removed in a future version.
       * @breaking-change 21.0.0 Remove this attribute.
       */
      this.defaultTabIndex = 0;
      this.tabIndexInputBinding = Number(tabIndex) || this.defaultTabIndex;
    }
    // This is a workaround for https://github.com/angular/angular/issues/23091
    // In aot mode, the lifecycle hooks from parent class are not called.
    ngOnInit() {
      super.ngOnInit();
    }
    ngOnDestroy() {
      super.ngOnDestroy();
    }
    static {
      this.ɵfac = function MatTreeNode_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatTreeNode)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.CdkTree), i0.ɵɵinjectAttribute('tabindex'));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatTreeNode,
        selectors: [["mat-tree-node"]],
        hostAttrs: [1, "mat-tree-node"],
        hostVars: 5,
        hostBindings: function MatTreeNode_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("click", function MatTreeNode_click_HostBindingHandler() {
              return ctx._focusItem();
            });
          }
          if (rf & 2) {
            i0.ɵɵhostProperty("tabindex", ctx._getTabindexAttribute());
            i0.ɵɵattribute("aria-expanded", ctx._getAriaExpanded())("aria-level", ctx.level + 1)("aria-posinset", ctx._getPositionInSet())("aria-setsize", ctx._getSetSize());
          }
        },
        inputs: {
          tabIndexInputBinding: [2, "tabIndex", "tabIndexInputBinding", value => value == null ? 0 : numberAttribute(value)],
          disabled: [2, "disabled", "disabled", booleanAttribute]
        },
        outputs: {
          activation: "activation",
          expandedChange: "expandedChange"
        },
        exportAs: ["matTreeNode"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTreeNode,
          useExisting: MatTreeNode
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return MatTreeNode;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * Wrapper for the CdkTree node definition with Material design styles.
 * Captures the node's template and a when predicate that describes when this node should be used.
 */
let MatTreeNodeDef = /*#__PURE__*/(() => {
  class MatTreeNodeDef extends CdkTreeNodeDef {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵMatTreeNodeDef_BaseFactory;
        return function MatTreeNodeDef_Factory(__ngFactoryType__) {
          return (ɵMatTreeNodeDef_BaseFactory || (ɵMatTreeNodeDef_BaseFactory = i0.ɵɵgetInheritedFactory(MatTreeNodeDef)))(__ngFactoryType__ || MatTreeNodeDef);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatTreeNodeDef,
        selectors: [["", "matTreeNodeDef", ""]],
        inputs: {
          when: [0, "matTreeNodeDefWhen", "when"],
          data: [0, "matTreeNode", "data"]
        },
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTreeNodeDef,
          useExisting: MatTreeNodeDef
        }]), i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return MatTreeNodeDef;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
/**
 * Wrapper for the CdkTree nested node with Material design styles.
 */
let MatNestedTreeNode = /*#__PURE__*/(() => {
  class MatNestedTreeNode extends CdkNestedTreeNode {
    /**
     * Whether the node is disabled.
     *
     * @deprecated This is an alias for `isDisabled`.
     * @breaking-change 21.0.0 Remove this input
     */
    get disabled() {
      return this.isDisabled;
    }
    set disabled(value) {
      this.isDisabled = value;
    }
    /** Tabindex of the node. */
    get tabIndex() {
      return this.isDisabled ? -1 : this._tabIndex;
    }
    set tabIndex(value) {
      // If the specified tabIndex value is null or undefined, fall back to the default value.
      this._tabIndex = value;
    }
    constructor(elementRef, tree, differs,
    // Ignore tabindex attribute. MatTree manages its own active state using TreeKeyManager.
    // Keeping tabIndex in constructor for backwards compatibility with trees created before
    // introducing TreeKeyManager.
    tabIndex) {
      super(elementRef, tree, differs);
    }
    // This is a workaround for https://github.com/angular/angular/issues/19145
    // In aot mode, the lifecycle hooks from parent class are not called.
    // TODO(tinayuangao): Remove when the angular issue #19145 is fixed
    ngOnInit() {
      super.ngOnInit();
    }
    ngAfterContentInit() {
      super.ngAfterContentInit();
    }
    ngOnDestroy() {
      super.ngOnDestroy();
    }
    static {
      this.ɵfac = function MatNestedTreeNode_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatNestedTreeNode)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.CdkTree), i0.ɵɵdirectiveInject(i0.IterableDiffers), i0.ɵɵinjectAttribute('tabindex'));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatNestedTreeNode,
        selectors: [["mat-nested-tree-node"]],
        hostAttrs: [1, "mat-nested-tree-node"],
        inputs: {
          node: [0, "matNestedTreeNode", "node"],
          disabled: [2, "disabled", "disabled", booleanAttribute],
          tabIndex: [2, "tabIndex", "tabIndex", value => value == null ? 0 : numberAttribute(value)]
        },
        outputs: {
          activation: "activation",
          expandedChange: "expandedChange"
        },
        exportAs: ["matNestedTreeNode"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkNestedTreeNode,
          useExisting: MatNestedTreeNode
        }, {
          provide: CdkTreeNode,
          useExisting: MatNestedTreeNode
        }, {
          provide: CDK_TREE_NODE_OUTLET_NODE,
          useExisting: MatNestedTreeNode
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return MatNestedTreeNode;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Wrapper for the CdkTree padding with Material design styles.
 */
let MatTreeNodePadding = /*#__PURE__*/(() => {
  class MatTreeNodePadding extends CdkTreeNodePadding {
    /** The level of depth of the tree node. The padding will be `level * indent` pixels. */
    get level() {
      return this._level;
    }
    set level(value) {
      this._setLevelInput(value);
    }
    /** The indent for each level. Default number 40px from material design menu sub-menu spec. */
    get indent() {
      return this._indent;
    }
    set indent(indent) {
      this._setIndentInput(indent);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵMatTreeNodePadding_BaseFactory;
        return function MatTreeNodePadding_Factory(__ngFactoryType__) {
          return (ɵMatTreeNodePadding_BaseFactory || (ɵMatTreeNodePadding_BaseFactory = i0.ɵɵgetInheritedFactory(MatTreeNodePadding)))(__ngFactoryType__ || MatTreeNodePadding);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatTreeNodePadding,
        selectors: [["", "matTreeNodePadding", ""]],
        inputs: {
          level: [2, "matTreeNodePadding", "level", numberAttribute],
          indent: [0, "matTreeNodePaddingIndent", "indent"]
        },
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTreeNodePadding,
          useExisting: MatTreeNodePadding
        }]), i0.ɵɵInputTransformsFeature, i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return MatTreeNodePadding;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Outlet for nested CdkNode. Put `[matTreeNodeOutlet]` on a tag to place children dataNodes
 * inside the outlet.
 */
let MatTreeNodeOutlet = /*#__PURE__*/(() => {
  class MatTreeNodeOutlet {
    constructor(viewContainer, _node) {
      this.viewContainer = viewContainer;
      this._node = _node;
    }
    static {
      this.ɵfac = function MatTreeNodeOutlet_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatTreeNodeOutlet)(i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(CDK_TREE_NODE_OUTLET_NODE, 8));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatTreeNodeOutlet,
        selectors: [["", "matTreeNodeOutlet", ""]],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTreeNodeOutlet,
          useExisting: MatTreeNodeOutlet
        }])]
      });
    }
  }
  return MatTreeNodeOutlet;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Wrapper for the CdkTable with Material design styles.
 */
let MatTree = /*#__PURE__*/(() => {
  class MatTree extends CdkTree {
    constructor() {
      super(...arguments);
      // Outlets within the tree's template where the dataNodes will be inserted.
      // We need an initializer here to avoid a TS error. The value will be set in `ngAfterViewInit`.
      this._nodeOutlet = undefined;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵMatTree_BaseFactory;
        return function MatTree_Factory(__ngFactoryType__) {
          return (ɵMatTree_BaseFactory || (ɵMatTree_BaseFactory = i0.ɵɵgetInheritedFactory(MatTree)))(__ngFactoryType__ || MatTree);
        };
      })();
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: MatTree,
        selectors: [["mat-tree"]],
        viewQuery: function MatTree_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(MatTreeNodeOutlet, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._nodeOutlet = _t.first);
          }
        },
        hostAttrs: [1, "mat-tree"],
        exportAs: ["matTree"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTree,
          useExisting: MatTree
        }]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵStandaloneFeature],
        decls: 1,
        vars: 0,
        consts: [["matTreeNodeOutlet", ""]],
        template: function MatTree_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementContainer(0, 0);
          }
        },
        dependencies: [MatTreeNodeOutlet],
        styles: [".mat-tree{display:block;background-color:var(--mat-tree-container-background-color, var(--mat-app-surface))}.mat-tree-node,.mat-nested-tree-node{color:var(--mat-tree-node-text-color, var(--mat-app-on-surface));font-family:var(--mat-tree-node-text-font, var(--mat-app-body-large-font));font-size:var(--mat-tree-node-text-size, var(--mat-app-body-large-size));font-weight:var(--mat-tree-node-text-weight, var(--mat-app-body-large-weight))}.mat-tree-node{display:flex;align-items:center;flex:1;word-wrap:break-word;min-height:var(--mat-tree-node-min-height)}.mat-nested-tree-node{border-bottom-width:0}"],
        encapsulation: 2
      });
    }
  }
  return MatTree;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Wrapper for the CdkTree's toggle with Material design styles.
 */
let MatTreeNodeToggle = /*#__PURE__*/(() => {
  class MatTreeNodeToggle extends CdkTreeNodeToggle {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵMatTreeNodeToggle_BaseFactory;
        return function MatTreeNodeToggle_Factory(__ngFactoryType__) {
          return (ɵMatTreeNodeToggle_BaseFactory || (ɵMatTreeNodeToggle_BaseFactory = i0.ɵɵgetInheritedFactory(MatTreeNodeToggle)))(__ngFactoryType__ || MatTreeNodeToggle);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: MatTreeNodeToggle,
        selectors: [["", "matTreeNodeToggle", ""]],
        inputs: {
          recursive: [0, "matTreeNodeToggleRecursive", "recursive"]
        },
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: CdkTreeNodeToggle,
          useExisting: MatTreeNodeToggle
        }]), i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return MatTreeNodeToggle;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MAT_TREE_DIRECTIVES = [MatNestedTreeNode, MatTreeNodeDef, MatTreeNodePadding, MatTreeNodeToggle, MatTree, MatTreeNode, MatTreeNodeOutlet];
let MatTreeModule = /*#__PURE__*/(() => {
  class MatTreeModule {
    static {
      this.ɵfac = function MatTreeModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MatTreeModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: MatTreeModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CdkTreeModule, MatCommonModule, MatCommonModule]
      });
    }
  }
  return MatTreeModule;
})();
/*#__PURE__*/(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Tree flattener to convert a normal type of node to node with children & level information.
 * Transform nested nodes of type `T` to flattened nodes of type `F`.
 *
 * For example, the input data of type `T` is nested, and contains its children data:
 *   SomeNode: {
 *     key: 'Fruits',
 *     children: [
 *       NodeOne: {
 *         key: 'Apple',
 *       },
 *       NodeTwo: {
 *        key: 'Pear',
 *      }
 *    ]
 *  }
 *  After flattener flatten the tree, the structure will become
 *  SomeNode: {
 *    key: 'Fruits',
 *    expandable: true,
 *    level: 1
 *  },
 *  NodeOne: {
 *    key: 'Apple',
 *    expandable: false,
 *    level: 2
 *  },
 *  NodeTwo: {
 *   key: 'Pear',
 *   expandable: false,
 *   level: 2
 * }
 * and the output flattened type is `F` with additional information.
 *
 * @deprecated Use MatTree#childrenAccessor and MatTreeNode#isExpandable
 * instead. To be removed in a future version.
 * @breaking-change 21.0.0
 */
class MatTreeFlattener {
  constructor(transformFunction, getLevel, isExpandable, getChildren) {
    this.transformFunction = transformFunction;
    this.getLevel = getLevel;
    this.isExpandable = isExpandable;
    this.getChildren = getChildren;
  }
  _flattenNode(node, level, resultNodes, parentMap) {
    const flatNode = this.transformFunction(node, level);
    resultNodes.push(flatNode);
    if (this.isExpandable(flatNode)) {
      const childrenNodes = this.getChildren(node);
      if (childrenNodes) {
        if (Array.isArray(childrenNodes)) {
          this._flattenChildren(childrenNodes, level, resultNodes, parentMap);
        } else {
          childrenNodes.pipe(take(1)).subscribe(children => {
            this._flattenChildren(children, level, resultNodes, parentMap);
          });
        }
      }
    }
    return resultNodes;
  }
  _flattenChildren(children, level, resultNodes, parentMap) {
    children.forEach((child, index) => {
      let childParentMap = parentMap.slice();
      childParentMap.push(index != children.length - 1);
      this._flattenNode(child, level + 1, resultNodes, childParentMap);
    });
  }
  /**
   * Flatten a list of node type T to flattened version of node F.
   * Please note that type T may be nested, and the length of `structuredData` may be different
   * from that of returned list `F[]`.
   */
  flattenNodes(structuredData) {
    let resultNodes = [];
    structuredData.forEach(node => this._flattenNode(node, 0, resultNodes, []));
    return resultNodes;
  }
  /**
   * Expand flattened node with current expansion status.
   * The returned list may have different length.
   */
  expandFlattenedNodes(nodes, treeControl) {
    let results = [];
    let currentExpand = [];
    currentExpand[0] = true;
    nodes.forEach(node => {
      let expand = true;
      for (let i = 0; i <= this.getLevel(node); i++) {
        expand = expand && currentExpand[i];
      }
      if (expand) {
        results.push(node);
      }
      if (this.isExpandable(node)) {
        currentExpand[this.getLevel(node) + 1] = treeControl.isExpanded(node);
      }
    });
    return results;
  }
}
/**
 * Data source for flat tree.
 * The data source need to handle expansion/collapsion of the tree node and change the data feed
 * to `MatTree`.
 * The nested tree nodes of type `T` are flattened through `MatTreeFlattener`, and converted
 * to type `F` for `MatTree` to consume.
 *
 * @deprecated Use one of levelAccessor or childrenAccessor instead. To be removed in a future
 * version.
 * @breaking-change 21.0.0
 */
class MatTreeFlatDataSource extends DataSource {
  get data() {
    return this._data.value;
  }
  set data(value) {
    this._data.next(value);
    this._flattenedData.next(this._treeFlattener.flattenNodes(this.data));
    this._treeControl.dataNodes = this._flattenedData.value;
  }
  constructor(_treeControl, _treeFlattener, initialData) {
    super();
    this._treeControl = _treeControl;
    this._treeFlattener = _treeFlattener;
    this._flattenedData = new BehaviorSubject([]);
    this._expandedData = new BehaviorSubject([]);
    this._data = new BehaviorSubject([]);
    if (initialData) {
      // Assign the data through the constructor to ensure that all of the logic is executed.
      this.data = initialData;
    }
  }
  connect(collectionViewer) {
    return merge(collectionViewer.viewChange, this._treeControl.expansionModel.changed, this._flattenedData).pipe(map(() => {
      this._expandedData.next(this._treeFlattener.expandFlattenedNodes(this._flattenedData.value, this._treeControl));
      return this._expandedData.value;
    }));
  }
  disconnect() {
    // no op
  }
}

/**
 * Data source for nested tree.
 *
 * The data source for nested tree doesn't have to consider node flattener, or the way to expand
 * or collapse. The expansion/collapsion will be handled by TreeControl and each non-leaf node.
 */
class MatTreeNestedDataSource extends DataSource {
  constructor() {
    super(...arguments);
    this._data = new BehaviorSubject([]);
  }
  /**
   * Data for the nested tree
   */
  get data() {
    return this._data.value;
  }
  set data(value) {
    this._data.next(value);
  }
  connect(collectionViewer) {
    return merge(...[collectionViewer.viewChange, this._data]).pipe(map(() => this.data));
  }
  disconnect() {
    // no op
  }
}

/**
 * Generated bundle index. Do not edit.
 */

export { MatNestedTreeNode, MatTree, MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule, MatTreeNestedDataSource, MatTreeNode, MatTreeNodeDef, MatTreeNodeOutlet, MatTreeNodePadding, MatTreeNodeToggle };
